// A task group's VPN on its page (D80): whether its tasks require the VPN, its environment's
// VPN state with a link to manage it (for those who may), and why steps wait.
import { Warning } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { accessQuery } from "../auth/access";
import { useT } from "../i18n";
import type { TaskGroup } from "../tasks/model";
import { Pill } from "../ui/status";
import { mayOpenShell } from "../workloads/environment/shell";
import { environmentVpnQuery, vpnState } from "./api";
import { VpnStatusPill } from "./VpnStatus";

export function GroupVpnLine({ group }: { group: Pick<TaskGroup, "environment_id" | "org_id" | "requires_vpn"> }) {
  const t = useT().vpn;
  const me = useQuery(accessQuery());
  // A task group's org is always its owner's personal one (D52).
  const mine = mayOpenShell(me.data, group.org_id, true);
  const env = group.environment_id;
  const vpn = useQuery({ ...environmentVpnQuery(env ?? ""), enabled: mine && !!env });
  const href = env ? `/environments/${env}/vpn?org=${group.org_id}` : null;
  const state = vpn.data ? vpnState(vpn.data) : null;
  const noProfile = group.requires_vpn && vpn.isSuccess && !vpn.data.settings;
  return (
    <span className="flex flex-col items-start gap-1.5 font-sans">
      <span className="flex flex-wrap items-center gap-2">
        {group.requires_vpn ? <Pill tone="primary">{t.group.requiredBadge}</Pill> : null}
        {state && href ? (
          <Link to={href} aria-label={t.link(t.state[state])}>
            <VpnStatusPill state={state} />
          </Link>
        ) : null}
        {!state && !group.requires_vpn ? <span className="text-gray-500">—</span> : null}
      </span>
      {noProfile ? (
        <span role="alert" className="flex items-start gap-1.5 text-[12.5px] text-orange-dark">
          <Warning weight="fill" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {t.group.noProfile}{" "}
            {href ? (
              <Link to={href} className="font-medium text-primary hover:underline">
                {t.group.setUp}
              </Link>
            ) : null}
          </span>
        </span>
      ) : null}
      {group.requires_vpn && !noProfile && state && state !== "up" ? (
        <span className="text-[12.5px] text-orange-dark">{t.group.waiting}</span>
      ) : null}
    </span>
  );
}
